import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DropsService } from '../_services/drops.service';
import { DropsDialogComponent } from './drops-dialog.component';

@Component({
  selector: 'app-drops',
  templateUrl: './drops.component.html',
  styleUrls: ['./drops.component.scss'],
})
export class DropsComponent implements OnInit {
  loading = true;
  saving = false;
  error = '';
  filter: 'all' | 'upcoming' = 'upcoming';
  today = '';
  suggestionNote = '';
  drops: any[] = [];
  suggestions: any[] = [];
  manualSuggestions: any[] = [];
  searchQuery = '';
  searching = false;
  searchError = '';
  searchResults: any[] = [];

  constructor(private dropsService: DropsService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.load();
  }

  get visibleDrops(): any[] {
    if (this.filter === 'upcoming') {
      return this.drops.filter((drop) => drop.daysUntil != null && drop.daysUntil >= 0);
    }
    return this.drops;
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.dropsService.list().subscribe(
      (data) => {
        this.today = data.today;
        this.drops = (data.drops || []).map((drop) => ({ ...drop, editValue: drop.value }));
        const listed = new Set(this.drops.map((drop) => drop.styleId));
        const automatic = data.suggestions || [];
        const automaticIds = new Set(automatic.map((shoe) => shoe.styleId));
        this.manualSuggestions = this.manualSuggestions.filter(
          (shoe) => !listed.has(shoe.styleId) && !automaticIds.has(shoe.styleId)
        );
        this.suggestions = this.manualSuggestions.concat(automatic);
        this.suggestionNote = data.suggestionNote || '';
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.error = 'Could not load drops.';
      }
    );
  }

  daysLabel(drop: any): string {
    if (drop.daysUntil == null || drop.daysUntil < 0) return '';
    if (drop.daysUntil === 0) return 'Today';
    if (drop.daysUntil === 1) return '1 day';
    return drop.daysUntil + ' days';
  }

  releaseMismatch(item: any): boolean {
    return !!(item.goatReleaseDate && item.releaseDate && item.goatReleaseDate !== item.releaseDate);
  }

  valueMismatch(drop: any): boolean {
    return drop.currentValue != null && Number(drop.currentValue) !== Number(drop.value);
  }

  listedValueChanged(drop: any): boolean {
    const next = Number(drop.editValue);
    return Number.isFinite(next) && next > 0 && next !== Number(drop.value);
  }

  saveListedValue(drop: any): void {
    if (!this.listedValueChanged(drop) || drop.savingValue) return;
    drop.savingValue = true;
    this.error = '';
    this.dropsService.updateValue(drop.id, Number(drop.editValue)).subscribe(
      (result) => {
        drop.value = result.value;
        drop.editValue = result.value;
        drop.savingValue = false;
      },
      () => {
        drop.savingValue = false;
        this.error = 'Could not update that listed price.';
      }
    );
  }

  openSelections(drop: any): void {
    this.dialog.open(DropsDialogComponent, {
      width: '1100px',
      data: { mode: 'selections', title: drop.title, drop },
    });
  }

  openInsight(drop: any): void {
    this.dialog.open(DropsDialogComponent, {
      width: '420px',
      data: { mode: 'insight', title: drop.title, drop },
    });
  }

  confirmDelete(drop: any): void {
    const ref = this.dialog.open(DropsDialogComponent, {
      width: '420px',
      data: { mode: 'delete', title: 'Delete this drop?', drop },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.saving = true;
      this.dropsService.remove(drop.id).subscribe(
        () => {
          this.saving = false;
          this.load();
        },
        () => {
          this.saving = false;
          this.error = 'Could not delete that drop.';
        }
      );
    });
  }

  searchShoes(): void {
    const query = (this.searchQuery || '').trim();
    if (query.length < 2 || this.searching) return;
    this.searching = true;
    this.searchError = '';
    this.searchResults = [];
    this.dropsService.search(query).subscribe(
      (data) => {
        this.searchResults = data.results || [];
        this.searching = false;
        if (!this.searchResults.length) this.searchError = 'No shoes matched that search.';
      },
      () => {
        this.searching = false;
        this.searchError = 'Could not search shoes.';
      }
    );
  }

  suggestionHas(shoe: any): boolean {
    return this.suggestions.some((item) => item.styleId === shoe.styleId);
  }

  addToSuggestions(shoe: any): void {
    if (!shoe || shoe.alreadyListed || this.suggestionHas(shoe)) return;
    this.manualSuggestions = [shoe].concat(
      this.manualSuggestions.filter((item) => item.styleId !== shoe.styleId)
    );
    this.suggestions = [shoe].concat(this.suggestions.filter((item) => item.styleId !== shoe.styleId));
  }

  confirmAdd(suggestion: any): void {
    const ref = this.dialog.open(DropsDialogComponent, {
      width: '420px',
      data: { mode: 'add', title: 'Add this drop?', suggestion },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.saving = true;
      this.dropsService.add(suggestion.styleId).subscribe(
        () => {
          this.saving = false;
          this.load();
        },
        () => {
          this.saving = false;
          this.error = 'Could not add that drop.';
        }
      );
    });
  }
}
